import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Reusable bottom-of-table pagination control.
 * Shows "Showing X–Y from N" + numbered page buttons + prev/next.
 */
export default function TablePagination({
    total,
    page,           // 1-based
    pageSize,
    onPageChange,
}) {
    const { t, isRTL } = useI18n();
    const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 10)));
    const safePage = Math.min(Math.max(page || 1, 1), totalPages);
    const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, total || 0);

    const pages = buildPageList(safePage, totalPages);

    if (totalPages <= 1) {
        return (
            <div className="px-5 py-3 border-t border-border flex items-center justify-between">
                <p className="text-sm text-muted-foreground" data-testid="table-pagination-info">
                    {t("table.showingFrom", { start, end, total: total || 0 })}
                </p>
            </div>
        );
    }

    return (
        <div className="px-5 py-3 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-muted-foreground" data-testid="table-pagination-info">
                {t("table.showingFrom", { start, end, total: total || 0 })}
            </p>
            <nav className="flex items-center gap-1" aria-label="Pagination">
                <button
                    type="button"
                    onClick={() => onPageChange(safePage - 1)}
                    disabled={safePage <= 1}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border text-sm hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label={t("table.prevPage")}
                    data-testid="table-pagination-prev"
                >
                    {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </button>
                {pages.map((p, i) =>
                    p === "..." ? (
                        <span key={`gap-${i}`} className="h-8 w-8 inline-flex items-center justify-center text-muted-foreground">…</span>
                    ) : (
                        <button
                            type="button"
                            key={p}
                            onClick={() => onPageChange(p)}
                            className={`h-8 min-w-8 px-2 inline-flex items-center justify-center rounded-md text-sm border ${
                                p === safePage
                                    ? "bg-foreground text-background border-foreground"
                                    : "border-border hover:bg-accent"
                            }`}
                            data-testid={`table-pagination-page-${p}`}
                            aria-current={p === safePage ? "page" : undefined}
                        >
                            {p}
                        </button>
                    )
                )}
                <button
                    type="button"
                    onClick={() => onPageChange(safePage + 1)}
                    disabled={safePage >= totalPages}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border text-sm hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label={t("table.nextPage")}
                    data-testid="table-pagination-next"
                >
                    {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
            </nav>
        </div>
    );
}

function buildPageList(current, total) {
    // Compact: 1 … (current-1) current (current+1) … total
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = new Set([1, total, current, current - 1, current + 1]);
    if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
    if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
    const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("...");
        out.push(sorted[i]);
    }
    return out;
}
